import React, { useState, useEffect, useMemo } from 'react';
import {
  ThumbsUp, Check, Loader, ArrowRight,
  Film, Tv, Sparkles, Users, Play,
  TrendingUp, Award, Crown, Zap, BarChart3,
  ExternalLink, Timer, Layers,
} from 'lucide-react';
import { getAuthHeaders } from '../services/api';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const SORA = "'Sora', system-ui, sans-serif";
const MANROPE = "'Manrope', system-ui, sans-serif";

const c = {
  bg: '#0a0a0f',
  card: 'linear-gradient(180deg,#151420,#101018)',
  cardFlat: '#151420',
  surface: '#181722',
  border: '#232130',
  borderSoft: '#2a2836',
  text: '#f4f3f7',
  textSub: '#a8a5b4',
  textMuted: '#8f8b9c',
  purple: '#8b5cf6',
  purpleLight: '#c4b5fd',
  pink: '#ec4899',
  green: '#34d399',
  greenLight: '#6ee7b7',
  amber: '#f59e0b',
  gold: '#fbbf24',
  blue: '#3b82f6',
  orange: '#f97316',
  cyan: '#06b6d4',
};

const CATEGORIES = {
  all: { label: 'All', icon: Zap, color: c.purple },
  media_request: { label: 'Media', icon: Film, color: c.purple },
  reaction_video: { label: 'Reactions', icon: Play, color: c.pink },
  reactor: { label: 'Reactors', icon: Users, color: c.blue },
  general: { label: 'General', icon: BarChart3, color: c.cyan },
};

const TYPE_CFG = {
  Anime:    { color: c.orange, gradient: `linear-gradient(135deg, ${c.orange}, #fb923c)`, icon: Sparkles },
  Movie:    { color: c.purple, gradient: `linear-gradient(135deg, ${c.purple}, ${c.pink})`, icon: Film },
  'TV Show':{ color: c.green,  gradient: `linear-gradient(135deg, ${c.green}, #4ade80)`, icon: Tv },
  Reactor:  { color: c.blue,   gradient: `linear-gradient(135deg, ${c.blue}, #60a5fa)`, icon: Users },
  Reaction: { color: c.pink,   gradient: `linear-gradient(135deg, ${c.pink}, #f472b6)`, icon: Play },
  General:  { color: c.textMuted, gradient: 'linear-gradient(135deg, #52525b, #71717a)', icon: BarChart3 },
};

// Deck fan: rotation + vertical offset cycle, matching the design mock
const DECK_ROTATIONS = [-7, 5, -4, 6, -5, 4];

const keyframes = `
@keyframes cv-spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
@keyframes cv-fadeUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}
@keyframes cv-pulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:.3;transform:scale(1.5)}}
`;

// ── helpers ──────────────────────────────────────────────────────────────────

const Spinner = ({ size = 20 }) => (
  <Loader style={{ width: size, height: size, animation: 'cv-spin 1s linear infinite', color: c.textSub }} />
);

function getLeader(items) {
  if (!items || items.length === 0) return null;
  const highest = Math.max(...items.map(i => i.voteCount || 0), 0);
  if (highest <= 0) return null;
  const leaders = items.filter(i => (i.voteCount || 0) === highest);
  return leaders.length === 1 ? leaders[0] : null;
}

function formatCountdown(msLeft) {
  if (msLeft <= 0) return null;
  const totalMinutes = Math.floor(msLeft / 60000);
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  const seconds = Math.floor((msLeft % 60000) / 1000);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m ${seconds}s`;
}

// Aspect ratio per option type — reaction videos are 16:9, posters are 2:3
const aspectFor = (type) => (type === 'Reaction' ? '16 / 9' : '2 / 3');

// Landscape thumbnails need a wider card to carry the same visual weight as a
// 2:3 poster — at 200px a 16:9 still is only ~112px tall and reads as a stub.
const widthFor = (type) => (type === 'Reaction' ? 320 : 200);

// Pull the 11-char video id out of any YouTube URL shape. Poll options created
// through the AI assistant often carry only videoUrl, so the thumbnail has to
// be derived from it rather than a stored videoId.
function extractYouTubeId(url) {
  if (!url) return null;
  const m = String(url).match(
    /(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/
  );
  return m ? m[1] : null;
}

function youTubeThumb(videoId, quality = 'maxresdefault') {
  return `https://img.youtube.com/vi/${videoId}/${quality}.jpg`;
}

// ── Stat pill ────────────────────────────────────────────────────────────────

const StatPill = ({ children, bg, color, border, dot }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: 8,
    background: bg, padding: '9px 16px', borderRadius: 999,
    fontSize: 13.5, fontWeight: 700, color,
    border: border || 'none', whiteSpace: 'nowrap',
  }}>
    {dot && (
      <span style={{
        width: 8, height: 8, borderRadius: '50%', background: '#fff',
        display: 'inline-block', animation: 'cv-pulse 1.6s ease-in-out infinite',
      }} />
    )}
    {children}
  </div>
);

// ── Deck Card (poster / thumbnail option) ────────────────────────────────────

const DeckCard = ({ item, pollId, userVotes, onVote, index, isHovered, onHover, isMobile }) => {
  const [imgSrc, setImgSrc] = useState(item.poster || null);
  const [imgErr, setImgErr] = useState(false);
  const cfg = TYPE_CFG[item.type] || TYPE_CFG.General;
  const Icon = cfg.icon;
  const pct = item.percentage || 0;
  const hasVoted = userVotes[pollId] === item.id;
  const pollVoted = userVotes[pollId] !== undefined;
  const leading = item._isLeader;
  const clickable = Boolean(item.url);

  const rotate = DECK_ROTATIONS[index % DECK_ROTATIONS.length];
  const offsetY = index % 2 === 0 ? 0 : 14;

  return (
    <div
      onMouseEnter={() => onHover(item.id)}
      onMouseLeave={() => onHover(null)}
      onClick={() => { if (clickable) window.open(item.url, '_blank'); }}
      style={{
        background: c.surface,
        borderRadius: isMobile ? 12 : 16,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        // The fan deck (fixed widths + tilt + overlap) only works with room to
        // spread. On phones the parent switches to a grid, so cards fill their
        // cell and drop the rotation/offset entirely.
        width: isMobile ? '100%' : widthFor(item.type),
        minWidth: 0,
        flexShrink: isMobile ? 1 : 0,
        // Mobile keeps the fan deck, just dialled down: ~40% of the desktop tilt
        // and offset so the cards still read as a deck inside their grid cells.
        marginTop: isMobile ? offsetY * 0.4 : offsetY,
        transform: isHovered
          ? `rotate(0deg) scale(${isMobile ? 1.06 : 1.12}) translateY(${isMobile ? -5 : -10}px)`
          : `rotate(${isMobile ? (rotate * 0.4).toFixed(1) : rotate}deg)`,
        zIndex: isHovered ? 40 : index + 1,
        border: hasVoted
          ? `2px solid ${c.green}`
          : leading ? `2px solid ${c.pink}` : `1px solid ${c.border}`,
        boxShadow: isHovered
          ? `0 ${isMobile ? 12 : 24}px ${isMobile ? 24 : 48}px rgba(139,92,246,0.4)`
          : leading ? '0 12px 30px rgba(236,72,153,0.3)' : '0 8px 20px rgba(0,0,0,0.4)',
        transition: 'transform 0.28s cubic-bezier(.34,1.56,.64,1), box-shadow 0.28s ease, margin 0.28s ease',
        cursor: clickable ? 'pointer' : 'default',
      }}
    >
      {leading && (
        <span style={{
          position: 'absolute', top: 10, left: 10, zIndex: 2,
          background: `linear-gradient(135deg, ${c.amber}, ${c.pink})`,
          color: '#fff', fontSize: isMobile ? 8 : 11, fontWeight: 700,
          padding: isMobile ? '2px 5px' : '5px 10px', borderRadius: 999,
          boxShadow: '0 4px 12px rgba(236,72,153,0.4)',
          display: 'flex', alignItems: 'center', gap: 4,
        }}>
          <Crown size={isMobile ? 8 : 11} /> {isMobile ? '#1' : 'LEADING'}
        </span>
      )}

      {/* media */}
      <div style={{ aspectRatio: aspectFor(item.type), position: 'relative', background: '#111' }}>
        {imgSrc && !imgErr ? (
          <img
            src={imgSrc}
            alt={item.title}
            onError={() => {
              // YouTube has no maxresdefault for many uploads — step down before giving up
              if (item.videoId && imgSrc.includes('maxresdefault')) {
                setImgSrc(youTubeThumb(item.videoId, 'hqdefault'));
              } else {
                setImgErr(true);
              }
            }}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        ) : (
          <div style={{
            width: '100%', height: '100%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: cfg.gradient,
          }}>
            <Icon size={46} style={{ color: 'rgba(255,255,255,0.55)' }} />
          </div>
        )}

        {clickable && (
          <span style={{
            position: 'absolute', top: 10, right: 10,
            padding: 5, background: 'rgba(10,10,15,0.7)', borderRadius: 6,
            opacity: isHovered ? 1 : 0, transition: 'opacity 0.2s',
            display: 'flex',
          }}>
            <ExternalLink size={12} style={{ color: '#fff' }} />
          </span>
        )}

        <span style={{
          position: 'absolute', bottom: isMobile ? 4 : 8, right: isMobile ? 4 : 8,
          background: 'rgba(10,10,15,0.85)', backdropFilter: 'blur(4px)',
          color: '#fff', fontFamily: SORA, fontWeight: 700, fontSize: isMobile ? 10 : 15,
          padding: isMobile ? '2px 5px' : '4px 10px', borderRadius: isMobile ? 5 : 8,
        }}>
          {pct.toFixed(0)}%
        </span>
      </div>

      {/* info */}
      <div style={{ padding: isMobile ? 6 : 14, display: 'flex', flexDirection: 'column', gap: isMobile ? 5 : 8, flex: 1 }}>
        <div>
          <h3 style={{
            fontSize: isMobile ? 11 : 16, fontWeight: 700, margin: 0, color: c.text, lineHeight: 1.25,
            overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box',
            WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
          }}>
            {item.title}
          </h3>
          {(item.year || item.genre || item.type) && (
            <p style={{ fontSize: isMobile ? 9.5 : 12.5, color: c.textMuted, margin: '2px 0 0' }}>
              {[item.year, item.genre || item.type].filter(Boolean).join(' · ')}
            </p>
          )}
        </div>

        <div style={{ height: isMobile ? 4 : 6, background: c.border, borderRadius: 99, overflow: 'hidden', marginTop: 'auto' }}>
          <div style={{
            height: '100%', width: `${pct}%`,
            background: hasVoted
              ? `linear-gradient(90deg, ${c.green}, ${c.greenLight})`
              : leading ? `linear-gradient(90deg, ${c.amber}, ${c.pink})` : `linear-gradient(90deg, ${c.purple}, ${c.pink})`,
            transition: 'width 0.4s ease',
          }} />
        </div>

        <span style={{ fontSize: isMobile ? 9.5 : 12, color: c.textMuted }}>
          {(item.voteCount || 0).toLocaleString()} {isMobile ? '' : ((item.voteCount || 0) === 1 ? 'vote' : 'votes')}
        </span>

        {!pollVoted && (
          <button
            type="button"
            onClick={e => { e.stopPropagation(); onVote(pollId, item.id, e); }}
            style={{
              background: `linear-gradient(135deg, ${c.purple}, ${c.pink})`,
              color: '#fff', border: 'none', borderRadius: isMobile ? 7 : 10,
              padding: isMobile ? 6 : 11,
              fontSize: isMobile ? 11 : 14, fontWeight: 700, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: isMobile ? 3 : 6,
              fontFamily: MANROPE,
            }}
          >
            Vote <ArrowRight size={isMobile ? 11 : 14} />
          </button>
        )}

        {hasVoted && (
          <span style={{
            background: 'rgba(52,211,153,0.15)', color: c.greenLight,
            textAlign: 'center', fontSize: isMobile ? 10 : 13, fontWeight: 700,
            padding: isMobile ? 6 : 10, borderRadius: isMobile ? 7 : 10,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: isMobile ? 3 : 6,
          }}>
            <Check size={isMobile ? 11 : 14} /> {isMobile ? 'Voted' : 'Your vote'}
          </span>
        )}
      </div>
    </div>
  );
};

// ── Reactor Row (channels stay a ranked leaderboard — avatars, not posters) ──

const RANK_BG = [
  'linear-gradient(135deg,#fbbf24,#f59e0b)',
  'linear-gradient(135deg,#d1d5db,#9ca3af)',
  'linear-gradient(135deg,#cd7f32,#92400e)',
];

const ReactorRow = ({ item, pollId, userVotes, onVote, rank, isHovered, onHover, isMobile }) => {
  const [imgErr, setImgErr] = useState(false);
  const cfg = TYPE_CFG[item.type] || TYPE_CFG.Reactor;
  const Icon = cfg.icon;
  const pct = item.percentage || 0;
  const hasVoted = userVotes[pollId] === item.id;
  const pollVoted = userVotes[pollId] !== undefined;
  const rankBg = RANK_BG[rank - 1];

  return (
    <div
      onMouseEnter={() => onHover(item.id)}
      onMouseLeave={() => onHover(null)}
      onClick={() => item.url && window.open(item.url, '_blank')}
      style={{
        display: 'flex', alignItems: 'center',
        gap: isMobile ? 10 : 16,
        flexWrap: isMobile ? 'wrap' : 'nowrap',
        padding: isMobile ? '12px' : '16px 18px',
        borderRadius: isMobile ? 12 : 16,
        background: isHovered ? 'rgba(255,255,255,0.04)' : c.surface,
        border: hasVoted ? `1.5px solid ${c.green}` : `1px solid ${c.border}`,
        boxShadow: isHovered ? '0 12px 30px rgba(139,92,246,0.18)' : '0 4px 14px rgba(0,0,0,0.3)',
        transform: isHovered ? `translateX(${isMobile ? 2 : 4}px)` : 'none',
        transition: 'transform 0.25s cubic-bezier(.34,1.56,.64,1), background 0.2s, box-shadow 0.25s',
        cursor: item.url ? 'pointer' : 'default',
      }}
    >
      <div style={{
        width: isMobile ? 26 : 32, height: isMobile ? 26 : 32,
        borderRadius: '50%', flexShrink: 0,
        background: rankBg || 'rgba(255,255,255,0.08)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: SORA, fontSize: isMobile ? 12 : 13, fontWeight: 800,
        color: rankBg ? (rank === 3 ? '#fff' : '#100c14') : c.textSub,
      }}>
        {rank}
      </div>

      <div style={{
        width: isMobile ? 42 : 56, height: isMobile ? 42 : 56,
        borderRadius: '50%', flexShrink: 0, overflow: 'hidden',
        border: `2px solid ${isHovered ? cfg.color : 'rgba(255,255,255,0.12)'}`,
        background: cfg.gradient,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'border-color 0.25s',
      }}>
        {item.poster && !imgErr ? (
          <img src={item.poster} alt={item.title} onError={() => setImgErr(true)}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <Icon size={22} style={{ color: 'rgba(255,255,255,0.7)' }} />
        )}
      </div>

      <div style={{ flex: 1, minWidth: isMobile ? 120 : 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 6 : 8, marginBottom: 8, flexWrap: 'wrap' }}>
          <p style={{
            margin: 0, fontSize: isMobile ? 14 : 15, fontWeight: 700, color: c.text,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            minWidth: 0, maxWidth: '100%',
          }}>
            {item.title}
          </p>
          {item._isLeader && (
            <span style={{
              padding: '3px 9px', borderRadius: 999, flexShrink: 0,
              background: `linear-gradient(135deg, ${c.amber}, ${c.pink})`,
              fontSize: 10, fontWeight: 700, color: '#fff',
              display: 'flex', alignItems: 'center', gap: 3,
            }}>
              <Crown size={10} /> LEADING
            </span>
          )}
          {hasVoted && (
            <span style={{
              padding: '3px 9px', borderRadius: 999, flexShrink: 0,
              background: 'rgba(52,211,153,0.15)', color: c.greenLight,
              fontSize: 10, fontWeight: 700,
            }}>
              YOUR VOTE
            </span>
          )}
        </div>
        <div style={{ height: 6, background: c.border, borderRadius: 99, overflow: 'hidden' }}>
          <div style={{
            height: '100%', width: `${pct}%`, borderRadius: 99,
            background: hasVoted
              ? `linear-gradient(90deg, ${c.green}, ${c.greenLight})`
              : item._isLeader ? `linear-gradient(90deg, ${c.amber}, ${c.pink})` : `linear-gradient(90deg, ${c.purple}, ${c.pink})`,
            transition: 'width 0.5s ease',
          }} />
        </div>
        <span style={{ fontSize: 12, color: c.textMuted, marginTop: 4, display: 'inline-block' }}>
          {(item.voteCount || 0).toLocaleString()} {(item.voteCount || 0) === 1 ? 'vote' : 'votes'}
        </span>
      </div>

      {/* On phones the percentage and Vote button move to their own row so the
          reactor name keeps a usable width instead of being squeezed to nothing. */}
      <div style={{
        display: 'flex', alignItems: 'center',
        gap: isMobile ? 10 : 16,
        flexShrink: 0,
        width: isMobile ? '100%' : undefined,
        justifyContent: isMobile ? 'space-between' : undefined,
      }}>
        <div style={{ textAlign: isMobile ? 'left' : 'right', flexShrink: 0, minWidth: 52 }}>
          <span style={{
            fontFamily: SORA, fontSize: isMobile ? 19 : 22, fontWeight: 800, lineHeight: 1,
            color: hasVoted ? c.greenLight : c.text,
          }}>
            {pct.toFixed(0)}
          </span>
          <span style={{ fontSize: 13, fontWeight: 600, color: c.textMuted }}>%</span>
        </div>

        {!pollVoted && (
          <button
            type="button"
            onClick={e => { e.stopPropagation(); onVote(pollId, item.id, e); }}
            style={{
              padding: isMobile ? '9px 16px' : '11px 20px',
              borderRadius: 10, border: 'none', flexShrink: 0,
              background: `linear-gradient(135deg, ${c.purple}, ${c.pink})`,
              color: '#fff', cursor: 'pointer',
              fontSize: isMobile ? 13 : 14, fontWeight: 700,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: 6, fontFamily: MANROPE, whiteSpace: 'nowrap',
            }}
          >
            Vote <ArrowRight size={14} />
          </button>
        )}
      </div>
    </div>
  );
};

// ── Poll Card ────────────────────────────────────────────────────────────────

const PollCard = ({ poll, userVotes, onVote, hoveredCard, onHover, now, isMobile }) => {
  const voted = userVotes[poll.id] !== undefined;
  const leader = getLeader(poll.items);
  const items = poll.items.map(i => ({ ...i, _isLeader: Boolean(leader && i.id === leader.id) }));
  const isReactorPoll = poll.items.length > 0 && poll.items.every(i => i.type === 'Reactor');
  const sorted = isReactorPoll
    ? [...items].sort((a, b) => (b.voteCount || 0) - (a.voteCount || 0))
    : items;
  // 16:9 thumbnails need the full width on a phone; 2:3 posters pair up fine
  const hasWideCards = sorted.some(i => i.type === 'Reaction');

  const msLeft = poll.endDate ? new Date(poll.endDate).getTime() - now : 0;
  const countdown = poll.endDate ? formatCountdown(msLeft) : null;

  return (
    <section style={{
      background: c.card,
      border: `1px solid ${c.border}`,
      borderRadius: isMobile ? 16 : 20,
      padding: isMobile ? 14 : 24,
      marginBottom: isMobile ? 14 : 24,
      animation: 'cv-fadeUp 0.4s ease-out both',
    }}>
      <div style={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        gap: 16, flexWrap: 'wrap', marginBottom: 20,
      }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{
              fontSize: 12, fontWeight: 700, letterSpacing: '0.06em',
              textTransform: 'uppercase', color: c.purple,
            }}>
              {poll.items.length} options
            </span>
            {poll.isFeatured && (
              <span style={{
                padding: '3px 9px', borderRadius: 999,
                background: `linear-gradient(135deg, ${c.orange}, ${c.gold})`,
                fontSize: 10, fontWeight: 800, color: '#100c14', letterSpacing: '0.04em',
              }}>
                ★ FEATURED
              </span>
            )}
            {countdown && (
              <span style={{
                display: 'flex', alignItems: 'center', gap: 5,
                padding: '3px 10px', borderRadius: 999,
                background: '#141018', border: `1.5px solid #b45309`,
                fontSize: 11, fontWeight: 700, color: c.gold,
              }}>
                <Timer size={11} /> Closes in {countdown}
              </span>
            )}
          </div>
          <h2 style={{
            fontFamily: SORA, fontWeight: 700, fontSize: 22, lineHeight: 1.3,
            margin: '4px 0 0', maxWidth: '42ch', color: c.text,
          }}>
            {poll.title}
          </h2>
          {poll.description && (
            <p style={{ margin: '6px 0 0', fontSize: 13.5, color: c.textSub, maxWidth: '60ch' }}>
              {poll.description}
            </p>
          )}
        </div>

        {voted && (
          <span style={{
            background: 'rgba(52,211,153,0.15)', color: c.greenLight,
            fontSize: 13, fontWeight: 600, padding: '6px 12px',
            borderRadius: 999, whiteSpace: 'nowrap',
            display: 'flex', alignItems: 'center', gap: 6,
          }}>
            <Check size={14} /> Vote counted
          </span>
        )}
      </div>

      {isReactorPoll ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {sorted.map((item, idx) => (
            <ReactorRow
              key={item.id}
              item={item}
              pollId={poll.id}
              userVotes={userVotes}
              onVote={onVote}
              rank={idx + 1}
              isHovered={hoveredCard === item.id}
              onHover={onHover}
              isMobile={isMobile}
            />
          ))}
        </div>
      ) : (
        <div style={isMobile ? {
          display: 'grid',
          gridTemplateColumns: hasWideCards ? '1fr' : `repeat(${sorted.length}, minmax(0, 1fr))`,
          gap: 6,
          padding: '4px 0 2px',
          alignItems: 'start',
        } : {
          display: 'flex', alignItems: 'flex-start', flexWrap: 'wrap',
          gap: 20, padding: '28px 10px 12px',
        }}>
          {sorted.map((item, idx) => (
            <DeckCard
              key={item.id}
              item={item}
              pollId={poll.id}
              userVotes={userVotes}
              onVote={onVote}
              index={idx}
              isHovered={hoveredCard === item.id}
              onHover={onHover}
              isMobile={isMobile}
            />
          ))}
        </div>
      )}
    </section>
  );
};

// ── Empty State ──────────────────────────────────────────────────────────────

const EmptyState = ({ filtered }) => (
  <section style={{
    background: c.card, border: `1px solid ${c.border}`,
    borderRadius: 20, padding: '40px 24px', textAlign: 'center',
  }}>
    <h2 style={{ fontFamily: SORA, fontWeight: 700, fontSize: 20, margin: 0, color: c.text }}>
      {filtered ? 'No ballots in this category yet' : 'No open ballots right now'}
    </h2>
    <p style={{ fontSize: 14.5, color: c.textSub, margin: '8px 0 0' }}>
      {filtered
        ? "Check back soon, or switch to All to see what's open now."
        : 'Check back soon — new polls are added regularly.'}
    </p>
  </section>
);

// ── How voting works ─────────────────────────────────────────────────────────

const HOW_IT_WORKS = [
  { icon: Sparkles, color: c.purple, title: 'Vote for what you want covered', body: "Every open ballot shapes what's next." },
  { icon: TrendingUp, color: c.pink, title: 'More votes, higher priority', body: 'The leader at close moves to the top.' },
  { icon: Layers, color: c.green, title: 'Results are always public', body: 'Live tallies, no hidden ranking.' },
  { icon: Award, color: c.gold, title: 'Reactors see the results', body: 'Creators use this to pick what to watch.' },
];

const HowItWorks = () => (
  <section style={{ paddingTop: 8 }}>
    <h2 style={{
      fontFamily: SORA, fontWeight: 700, fontSize: 18,
      margin: '0 0 16px', color: c.purpleLight,
    }}>
      How voting works
    </h2>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px,1fr))', gap: 14 }}>
      {HOW_IT_WORKS.map((item, i) => (
        <div key={i} style={{
          background: c.cardFlat, border: `1px solid ${c.border}`,
          borderRadius: 14, padding: 16,
        }}>
          <item.icon size={20} style={{ color: item.color, marginBottom: 8 }} />
          <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0, color: c.text }}>{item.title}</h3>
          <p style={{ fontSize: 13, color: c.textSub, margin: '6px 0 0', lineHeight: 1.5 }}>{item.body}</p>
        </div>
      ))}
    </div>
  </section>
);

// ── Main ─────────────────────────────────────────────────────────────────────

const VoteReactionsTab = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [hoveredCard, setHoveredCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [polls, setPolls] = useState([]);
  const [userVotes, setUserVotes] = useState({});
  const [now, setNow] = useState(Date.now());
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => { fetchPolls(); }, []);

  // Ticks the countdown pills once per second (only while a poll has an end date)
  const hasDeadline = polls.some(p => p.endDate);
  useEffect(() => {
    if (!hasDeadline) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [hasDeadline]);

  const fetchPolls = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/votes/polls`);
      const data = await res.json();
      if (!data.success || !data.polls?.length) { setPolls([]); return; }

      const results = await Promise.all(data.polls.map(async poll => {
        try {
          let pollResults = {};
          try {
            const r = await fetch(`${API_URL}/votes/polls/${poll._id}`);
            const d = await r.json();
            if (d.success && d.poll?.results) {
              d.poll.results.forEach(res => {
                pollResults[res.optionId] = { percentage: res.percentage || 0, topReactors: res.topReactors || [] };
              });
            }
          } catch {}

          const items = (poll.options || []).map(opt => {
            const type =
              opt.type === 'movie' ? 'Movie' :
              opt.type === 'tv' ? 'TV Show' :
              opt.type === 'anime' ? 'Anime' :
              opt.type === 'reactor_channel' ? 'Reactor' :
              opt.type === 'reaction_video' ? 'Reaction' : 'General';

            // videoId may be null on options saved with only a videoUrl
            const videoId = opt.videoId || extractYouTubeId(opt.videoUrl);

            let url = opt.videoUrl || null;
            if (type === 'Reactor' && opt.channelId) url = `https://youtube.com/channel/${opt.channelId}`;
            else if (type === 'Reaction' && videoId) url = `https://youtube.com/watch?v=${videoId}`;

            let poster = opt.poster || opt.thumbnail || null;
            if (!poster && videoId) poster = youTubeThumb(videoId);

            const res = pollResults[opt.optionId] || { percentage: 0, topReactors: [] };
            return {
              id: opt.optionId, pollId: poll._id, title: opt.title || '', type,
              year: opt.year, genre: opt.genre, poster,
              percentage: res.percentage, voteCount: opt.voteCount || 0,
              topReactors: res.topReactors, url,
              channelId: opt.channelId, videoId,
            };
          });

          checkUserVote(poll._id);
          return {
            id: poll._id,
            title: poll.title,
            description: poll.description || '',
            category: poll.category,
            isFeatured: poll.isFeatured || false,
            endDate: poll.endDate || null,
            totalVotes: poll.totalVotes || 0,
            uniqueVoters: poll.uniqueVoters || 0,
            items,
          };
        } catch { return null; }
      }));

      setPolls(results.filter(Boolean));
    } catch { setPolls([]); }
    finally { setLoading(false); }
  };

  const refreshPoll = async (pollId) => {
    try {
      const res = await fetch(`${API_URL}/votes/polls/${pollId}?showFull=true`);
      const data = await res.json();
      if (!data.success || !data.poll?.results) return;
      setPolls(prev => prev.map(poll => {
        if (poll.id !== pollId) return poll;
        return {
          ...poll,
          totalVotes: data.poll.totalVotes ?? poll.totalVotes,
          items: poll.items.map(item => {
            const r = data.poll.results.find(r => r.optionId === item.id);
            if (!r) return item;
            return {
              ...item,
              percentage: r.percentage,
              voteCount: r.voteCount ?? item.voteCount,
              topReactors: r.topReactors || [],
            };
          }),
        };
      }));
    } catch {}
  };

  const checkUserVote = async (pollId) => {
    try {
      const headers = getAuthHeaders();
      if (!headers.Authorization) return;
      const res = await fetch(`${API_URL}/votes/polls/${pollId}/check`, { headers });
      const data = await res.json();
      if (data.success && data.hasVoted) setUserVotes(prev => ({ ...prev, [pollId]: data.vote.optionId }));
    } catch {}
  };

  const handleVote = async (pollId, optionId, event) => {
    if (event) event.stopPropagation();
    const headers = getAuthHeaders();
    if (!headers.Authorization) { alert('Please log in to vote'); return; }
    if (userVotes[pollId]) { alert('Already voted in this poll'); return; }

    // Optimistic bump so the tally reacts immediately
    setUserVotes(prev => ({ ...prev, [pollId]: optionId }));
    setPolls(prev => prev.map(poll => poll.id !== pollId ? poll : {
      ...poll,
      totalVotes: (poll.totalVotes || 0) + 1,
      items: poll.items.map(i => i.id === optionId ? { ...i, voteCount: (i.voteCount || 0) + 1 } : i),
    }));

    try {
      const res = await fetch(`${API_URL}/votes/polls/${pollId}/vote`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ optionId }),
      });
      const data = await res.json();
      if (data.success) {
        await refreshPoll(pollId);
      } else {
        setUserVotes(prev => { const next = { ...prev }; delete next[pollId]; return next; });
        await refreshPoll(pollId);
        alert(data.error || 'Failed to submit vote');
      }
    } catch {
      setUserVotes(prev => { const next = { ...prev }; delete next[pollId]; return next; });
      await refreshPoll(pollId);
      alert('Failed to submit vote');
    }
  };

  const filtered = useMemo(() =>
    polls.filter(p => selectedCategory === 'all' || p.category === selectedCategory)
      .filter(p => p.items.length > 0),
    [polls, selectedCategory]
  );

  // Featured polls float to the top, everything else keeps API order
  const ordered = useMemo(() => {
    const featured = filtered.filter(p => p.isFeatured);
    const rest = filtered.filter(p => !p.isFeatured);
    return [...featured, ...rest];
  }, [filtered]);

  // Header stats — derived from real poll data only
  const totalVotesCast = useMemo(
    () => polls.reduce((sum, p) => sum + (p.totalVotes || 0), 0),
    [polls]
  );
  const openBallots = polls.length;
  const soonestDeadline = useMemo(() => {
    const upcoming = polls
      .map(p => (p.endDate ? new Date(p.endDate).getTime() : null))
      .filter(t => t && t > now)
      .sort((a, b) => a - b);
    return upcoming.length ? upcoming[0] : null;
  }, [polls, now]);
  const globalCountdown = soonestDeadline ? formatCountdown(soonestDeadline - now) : null;

  return (
    <div style={{ minHeight: '100vh', background: c.bg, color: c.text, fontFamily: MANROPE }}>
      <style>{keyframes}</style>

      <div style={{ maxWidth: 1180, margin: '0 auto', padding: isMobile ? '20px 14px 80px' : '40px clamp(20px,5vw,56px) 80px' }}>

        {/* ── Header ── */}
        <section style={{ paddingBottom: isMobile ? 18 : 28 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 12 : 16, marginBottom: isMobile ? 14 : 20, flexWrap: 'wrap' }}>
            <div style={{
              width: isMobile ? 44 : 56, height: isMobile ? 44 : 56,
              borderRadius: isMobile ? 13 : 16, flexShrink: 0,
              background: `linear-gradient(135deg, ${c.purple}, ${c.pink})`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 8px 24px rgba(139,92,246,0.35)',
            }}>
              <ThumbsUp size={isMobile ? 22 : 28} style={{ color: '#fff' }} />
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <h1 style={{
                fontFamily: SORA, fontWeight: 800,
                fontSize: isMobile ? '24px' : 'clamp(30px,4vw,42px)', lineHeight: 1.1,
                margin: 0, letterSpacing: '-0.01em', color: c.text,
              }}>
                Community Vote
              </h1>
              <p style={{ fontSize: isMobile ? 12.5 : 15, color: c.textSub, margin: '4px 0 0' }}>
                Shape what gets reacted to — your vote matters
              </p>
            </div>
          </div>

          {!loading && openBallots > 0 && (
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              <StatPill bg="#3b1673" color="#ede9fe">
                {totalVotesCast.toLocaleString()} {totalVotesCast === 1 ? 'vote' : 'votes'} cast
              </StatPill>
              <StatPill bg="#7e1450" color="#fce7f3" dot>
                {openBallots} {openBallots === 1 ? 'ballot' : 'ballots'} open
              </StatPill>
              {globalCountdown && (
                <StatPill bg="#141018" color={c.gold} border="1.5px solid #b45309">
                  <Timer size={13} /> Next closes in {globalCountdown}
                </StatPill>
              )}
            </div>
          )}
        </section>

        {/* ── Filters ── */}
        <section style={{ paddingBottom: 24 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {Object.entries(CATEGORIES).map(([key, cfg]) => {
              const Icon = cfg.icon;
              const active = selectedCategory === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setSelectedCategory(key)}
                  style={{
                    background: active ? `linear-gradient(135deg, ${c.purple}, ${c.pink})` : c.surface,
                    color: active ? '#fff' : '#c9c6d3',
                    border: active ? 'none' : `1px solid ${c.borderSoft}`,
                    borderRadius: 999, padding: '10px 18px',
                    fontSize: 14, fontWeight: active ? 700 : 600,
                    cursor: 'pointer', fontFamily: MANROPE,
                    display: 'flex', alignItems: 'center', gap: 7,
                    whiteSpace: 'nowrap', transition: 'background 0.2s, color 0.2s',
                  }}
                >
                  <Icon size={15} /> {cfg.label}
                </button>
              );
            })}
          </div>
        </section>

        {/* ── Content ── */}
        {loading ? (
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', padding: '120px 0', gap: 16,
          }}>
            <Spinner size={36} />
            <p style={{ margin: 0, fontSize: 14, color: c.textMuted }}>Loading ballots…</p>
          </div>
        ) : ordered.length === 0 ? (
          <EmptyState filtered={selectedCategory !== 'all'} />
        ) : (
          ordered.map(poll => (
            <PollCard
              key={poll.id}
              poll={poll}
              userVotes={userVotes}
              onVote={handleVote}
              hoveredCard={hoveredCard}
              onHover={setHoveredCard}
              now={now}
              isMobile={isMobile}
            />
          ))
        )}

        {/* ── How voting works ── */}
        {!loading && <HowItWorks />}
      </div>
    </div>
  );
};

export default VoteReactionsTab;
